import {
  LINEAR_CONVERSATION_MAX_ATTACHMENT_BYTES,
  LINEAR_CONVERSATION_MAX_ATTACHMENTS,
  LINEAR_CONVERSATION_MAX_BODY_LENGTH,
  LINEAR_CONVERSATION_MAX_REQUEST_BYTES,
  type LinearConversationAttachment,
  type LinearConversationComment,
  type LinearConversationCommentPage,
  type PostLinearConversationCommentInput,
} from "@repo/shared-types/conversation";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, Fragment, type Ref, useCallback, useEffect, useRef, useState } from "react";
import { RequestError } from "../client.ts";
import { Markdown } from "../Markdown.tsx";
import { Button, ErrorText, Feedback, Field, SkeletonList, Textarea } from "../ui/index.ts";

export function isConversationAccessDenied(error: unknown): boolean {
  return error instanceof RequestError && [401, 403, 404].includes(error.status);
}

export const MAX_CONVERSATION_ATTACHMENTS = LINEAR_CONVERSATION_MAX_ATTACHMENTS;
export const MAX_CONVERSATION_ATTACHMENT_BYTES = LINEAR_CONVERSATION_MAX_ATTACHMENT_BYTES;
export const MAX_CONVERSATION_REQUEST_BYTES = LINEAR_CONVERSATION_MAX_REQUEST_BYTES;
export const MAX_CONVERSATION_BODY_LENGTH = LINEAR_CONVERSATION_MAX_BODY_LENGTH;

export type ConversationAttachment = LinearConversationAttachment;

export type ConversationComment = Omit<LinearConversationComment, "authorIsViewer"> & {
  authorIsCurrentUser: boolean;
  isUnread?: boolean;
};

export type ConversationReadCursor = { createdAt: string; commentIds: string[] };
export type ConversationPage = Omit<LinearConversationCommentPage, "items"> & { items: readonly ConversationComment[]; readCursor?: ConversationReadCursor | null };
export type ConversationInput = PostLinearConversationCommentInput;

export type LinearConversationProps = {
  queryKey: readonly unknown[];
  load: (cursor: string | null) => Promise<ConversationPage>;
  post: (input: ConversationInput, files: readonly File[], onProgress: (progress: number) => void) => Promise<unknown>;
  canComment: boolean;
  available?: boolean;
  enabled?: boolean;
  refetchInterval?: number;
  activityVersion?: string | null;
  onLatestVisible?: (cursor: ConversationReadCursor) => Promise<void>;
  readDwellMs?: number;
  readThreshold?: number;
  idPrefix?: string;
  composerLabel?: string;
  composerHelp?: string;
  emptyMessage?: string;
  loadOlderLabel?: string;
  placeholder?: string;
  readOnlyMessage?: string;
  selectedFilesLabel?: string;
  submitLabel?: string;
  submitVariant?: "primary" | "secondary";
  successMessage?: string;
  commentErrorMessage: (error: unknown) => string;
  loadErrorMessage: (error: unknown) => string;
};

type PendingAttachment = { error?: string; file: File; id: string; progress: number };

const dateTime = (createdAt: string): string => new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(createdAt));

const errorCode = (error: unknown): string | null => {
  if (!(error instanceof RequestError) || typeof error.body !== "object" || error.body === null || !("error" in error.body)) return null;
  return typeof error.body.error === "string" ? error.body.error : null;
};

export const READ_DWELL_MS = 1_500;
export const READ_VISIBLE_RATIO = 0.6;

// A comment taller than the viewport can never reach the ratio itself, so filling the viewport counts too.
export const isDwellVisible = (entry: Pick<IntersectionObserverEntry, "isIntersecting" | "intersectionRatio" | "intersectionRect" | "rootBounds">, ratio: number): boolean =>
  entry.isIntersecting && (entry.intersectionRatio >= ratio || (entry.rootBounds !== null && entry.intersectionRect.height >= entry.rootBounds.height * ratio));

export const newReplyAnnouncement = (count: number): string => (count === 0 ? "" : `${count} new ${count === 1 ? "reply" : "replies"}`);

export const CONVERSATION_UNAVAILABLE_MESSAGE = "This conversation is unavailable. Contact the team.";

export const isConversationUnavailable = (error: unknown): boolean => errorCode(error) === "conversation_unavailable";

export const defaultConversationErrorMessage = (error: unknown): string => {
  if (errorCode(error) === "database_unavailable") return "The portal is temporarily unavailable. Try again in a moment.";
  if (errorCode(error) === "not_found") return "This conversation is no longer available.";
  return "The conversation could not be loaded. Try closing and reopening this page.";
};

export const defaultCommentErrorMessage = (error: unknown): string => {
  if (error instanceof RequestError && error.status === 413) return "Attachments must be under 10 MiB each and the complete comment under 50 MiB.";
  if (errorCode(error) === "invalid_state") return "This request has already been decided and can no longer receive comments.";
  if (errorCode(error) === "database_unavailable") return "The portal is temporarily unavailable. Your comment is still here; try again.";
  if (errorCode(error) === "linear_unavailable") return "Linear is temporarily unavailable. Your comment is still here; try again.";
  return "Your comment could not be sent. It is still here; try again.";
};

export const chronologicalConversation = (pages: readonly ConversationPage[]): ConversationComment[] => pages.flatMap((page) => page.items).reverse();

export const conversationInput = (body: string, idempotencyKey: string): ConversationInput => ({ ...(body.trim() ? { body: body.trim() } : {}), idempotencyKey });

export const ConversationAttachments = ({ attachments }: { attachments: readonly ConversationAttachment[] }) => {
  if (attachments.length === 0) return null;
  return (
    <ul className="conversation-attachments" aria-label="Attachments">
      {attachments.map((attachment) => (
        <li key={attachment.id}>
          {attachment.inline.kind === "image" ? (
            <img src={attachment.inline.url} alt={attachment.fileName} className="conversation-attachment-media" />
          ) : attachment.inline.kind === "video" ? (
            // biome-ignore lint/a11y/useMediaCaption: Uploaded videos do not have a caption source; controls let people pause or mute playback.
            <video className="conversation-attachment-media" controls preload="metadata">
              <source src={attachment.inline.url} {...(attachment.contentType ? { type: attachment.contentType } : {})} />
              <a href={attachment.downloadUrl} download={attachment.fileName}>
                Download {attachment.fileName}
              </a>
            </video>
          ) : null}
          <a href={attachment.downloadUrl} download={attachment.fileName} className="conversation-attachment-download">
            Download {attachment.fileName}
          </a>
        </li>
      ))}
    </ul>
  );
};

const ConversationCommentView = ({ comment, latestRef }: { comment: ConversationComment; latestRef?: Ref<HTMLLIElement> }) => (
  <li ref={latestRef} className="conversation-comment">
    <div className="conversation-comment__head">
      <span className="t-label">{comment.authorIsCurrentUser ? "You" : comment.authorName}</span>
      <time className="t-caption" dateTime={comment.createdAt}>
        {dateTime(comment.createdAt)}
      </time>
    </div>
    <div className="conversation-markdown conversation-comment__body">
      <Markdown>{comment.body}</Markdown>
    </div>
    <ConversationAttachments attachments={comment.attachments} />
  </li>
);

export const LinearConversation = ({
  queryKey,
  load,
  post,
  canComment,
  available = true,
  enabled = true,
  refetchInterval,
  activityVersion,
  onLatestVisible,
  readDwellMs = READ_DWELL_MS,
  readThreshold = READ_VISIBLE_RATIO,
  idPrefix = "conversation",
  composerLabel = "Comment",
  composerHelp = "Markdown is supported.",
  emptyMessage = "No comments yet.",
  loadOlderLabel = "Load older comments",
  placeholder = "Add a comment",
  readOnlyMessage = "This request has been decided. Comments are closed.",
  selectedFilesLabel = "Selected files",
  submitLabel = "Send comment",
  submitVariant = "secondary",
  successMessage = "Comment sent.",
  commentErrorMessage,
  loadErrorMessage,
}: LinearConversationProps) => {
  const queryClient = useQueryClient();
  const latestComment = useRef<HTMLLIElement>(null);
  const acknowledged = useRef<string | null>(null);
  const activity = useRef(activityVersion);
  const commentId = `${idPrefix}-comment`;
  const commentCountId = `${idPrefix}-comment-count`;
  const filesId = `${idPrefix}-files`;
  const [readError, setReadError] = useState<unknown>(null);
  const [announcement, setAnnouncement] = useState("");
  // Identical consecutive messages are only re-read by screen readers when the region is emptied first.
  const announce = useCallback((message: string): void => {
    setAnnouncement("");
    setTimeout(() => setAnnouncement(message), 0);
  }, []);
  const dividerId = useRef<string | null>(null);
  const dividerResolved = useRef(false);
  const knownIds = useRef<Set<string> | null>(null);
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const idempotencyKey = useRef(crypto.randomUUID());
  const comments = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => load(pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextCursor,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => (isConversationUnavailable(query.state.error) ? false : refetchInterval),
    refetchIntervalInBackground: false,
    enabled: enabled && available,
  });
  const send = useMutation({
    mutationFn: ({ files, input }: { files: readonly File[]; input: ConversationInput }) =>
      post(input, files, (progress) => setAttachments((current) => current.map((attachment) => ({ ...attachment, error: undefined, progress })))),
    onSuccess: async () => {
      setBody("");
      setAttachments([]);
      idempotencyKey.current = crypto.randomUUID();
      await queryClient.invalidateQueries({ queryKey });
    },
  });
  const displayedComments = chronologicalConversation(comments.data?.pages ?? []);
  const latestPage = comments.data?.pages[0];
  const readCursor = latestPage?.readCursor;
  const newestId = latestPage?.items[0]?.id;
  const cursorKey = readCursor ? JSON.stringify(readCursor) : null;
  const refetch = comments.refetch;
  if (comments.isSuccess && !dividerResolved.current) {
    dividerResolved.current = true;
    dividerId.current = displayedComments.find((comment) => comment.isUnread && !comment.authorIsCurrentUser)?.id ?? null;
  }
  useEffect(() => {
    if (!comments.data) return;
    const known = knownIds.current;
    const ids = new Set(chronologicalConversation(comments.data.pages).map((comment) => comment.id));
    knownIds.current = ids;
    if (!known) return;
    // Only the newest page can hold replies that arrived since the last render; older pages hold history.
    const arrived = (comments.data.pages[0]?.items ?? []).filter((comment) => !known.has(comment.id) && !comment.authorIsCurrentUser).length;
    if (arrived > 0) announce(newReplyAnnouncement(arrived));
  }, [comments.data, announce]);
  useEffect(() => {
    if (comments.isSuccess) setReadError(null);
  }, [comments.dataUpdatedAt, comments.isSuccess]);
  useEffect(() => {
    if (activity.current !== activityVersion && enabled && available) void refetch();
    activity.current = activityVersion;
  }, [activityVersion, enabled, available, refetch]);
  useEffect(() => {
    const element = latestComment.current;
    if (
      !enabled ||
      !available ||
      comments.isError ||
      isConversationAccessDenied(readError) ||
      isConversationUnavailable(readError) ||
      !onLatestVisible ||
      !readCursor ||
      !cursorKey ||
      !element ||
      !globalThis.IntersectionObserver
    )
      return;
    let visible = false;
    let sending = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const clearTimer = (): void => {
      if (timer) clearTimeout(timer);
      timer = null;
    };
    async function acknowledge(): Promise<void> {
      if (!visible || sending || document.visibilityState !== "visible" || acknowledged.current === cursorKey) return;
      sending = true;
      try {
        await onLatestVisible?.(readCursor as ConversationReadCursor);
        acknowledged.current = cursorKey;
      } catch (error) {
        if (isConversationAccessDenied(error)) setReadError(error);
        if (isConversationUnavailable(error)) setReadError(error);
        else if (error instanceof RequestError && error.status === 409) void refetch();
      } finally {
        sending = false;
      }
    }
    function startDwell(): void {
      clearTimer();
      if (!visible || document.visibilityState !== "visible") return;
      if (readDwellMs <= 0) {
        void acknowledge();
        return;
      }
      timer = setTimeout(() => {
        timer = null;
        void acknowledge();
      }, readDwellMs);
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        const nowVisible = entry ? isDwellVisible(entry, readThreshold) : false;
        if (nowVisible === visible) return;
        visible = nowVisible;
        startDwell();
      },
      { threshold: [0, readThreshold, 1] },
    );
    observer.observe(element);
    const onFocus = (): void => startDwell();
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      clearTimer();
      observer.disconnect();
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, [enabled, available, comments.isError, readError, cursorKey, onLatestVisible, readCursor, refetch, readDwellMs, readThreshold]);

  const validComment = (body.trim().length > 0 || attachments.length > 0) && body.length <= MAX_CONVERSATION_BODY_LENGTH;

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!available || !canComment || send.isPending || !validComment) return;
    const files = attachments.map((attachment) => attachment.file);
    if (files.some((file) => file.size > MAX_CONVERSATION_ATTACHMENT_BYTES)) return;
    if (files.reduce((size, file) => size + file.size, 0) >= MAX_CONVERSATION_REQUEST_BYTES) {
      setAttachments((current) => current.map((attachment) => ({ ...attachment, error: "Attachments must total less than 50 MiB." })));
      return;
    }
    try {
      await send.mutateAsync({ files, input: conversationInput(body, idempotencyKey.current) });
    } catch {
      // The mutation state renders the failure without clearing the draft or idempotency key.
    }
  };

  const unavailable = [comments.error, send.error, readError].some(isConversationUnavailable);
  const denied = [comments.error, send.error, readError].find(isConversationAccessDenied);
  // Keep an unsent draft through upstream outages without rendering unvalidated content.
  if (!available) return null;
  if (denied)
    return (
      <section className="linear-conversation" aria-label="Conversation">
        <ErrorText aria-label="Conversation access denied">{loadErrorMessage(denied)}</ErrorText>
      </section>
    );

  return (
    <section className="linear-conversation" aria-label="Conversation">
      <h2 className="t-title">Conversation</h2>
      {comments.isLoading && <SkeletonList rows={2} />}
      {comments.isError && <ErrorText aria-label="Conversation load error">{loadErrorMessage(comments.error)}</ErrorText>}
      {comments.hasNextPage && (
        <Button variant="ghost" disabled={comments.isFetchingNextPage} onClick={() => comments.fetchNextPage()}>
          {comments.isFetchingNextPage ? "Loading…" : loadOlderLabel}
        </Button>
      )}
      {comments.data && displayedComments.length === 0 && <Feedback aria-label="Conversation empty">{emptyMessage}</Feedback>}
      {displayedComments.length > 0 && (
        <ol className="conversation-comment-list">
          {displayedComments.map((comment) => (
            <Fragment key={comment.id}>
              {comment.id === dividerId.current && (
                <li className="conversation-divider">
                  <span className="conversation-divider__label">
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M8 2.5v11" />
                      <path d="m3.5 9 4.5 4.5L12.5 9" />
                    </svg>
                    New since your last visit
                  </span>
                </li>
              )}
              <ConversationCommentView comment={comment} latestRef={comment.id === newestId ? latestComment : undefined} />
            </Fragment>
          ))}
        </ol>
      )}
      <p className="sr-only" role="status">
        {announcement}
      </p>
      {canComment && !unavailable ? (
        <form className="conversation-form" onSubmit={(event) => void submit(event)}>
          <fieldset className="conversation-submit-fields" disabled={send.isPending}>
            <Field id={commentId} label={composerLabel} help={composerHelp}>
              <Textarea
                id={commentId}
                rows={4}
                maxLength={MAX_CONVERSATION_BODY_LENGTH}
                value={body}
                placeholder={placeholder}
                aria-describedby={commentCountId}
                onChange={(event) => {
                  setBody(event.target.value);
                  if (send.isSuccess || send.isError) send.reset();
                }}
              />
            </Field>
            <div className="conversation-attachment-picker">
              <label htmlFor={filesId} className="t-label">
                Attachments
              </label>
              <input
                id={filesId}
                type="file"
                multiple
                onChange={(event) => {
                  const files = Array.from(event.target.files ?? []).slice(0, Math.max(0, MAX_CONVERSATION_ATTACHMENTS - attachments.length));
                  setAttachments((current) => [
                    ...current,
                    ...files.map((file) => ({ ...(file.size > MAX_CONVERSATION_ATTACHMENT_BYTES ? { error: "File must be 10 MiB or smaller." } : {}), file, id: crypto.randomUUID(), progress: 0 })),
                  ]);
                  event.target.value = "";
                }}
              />
              <p className="t-caption">Up to 10 files, 10 MiB each. Files upload only when you send.</p>
              {attachments.length > 0 && (
                <ul className="conversation-pending-attachments" aria-label={selectedFilesLabel}>
                  {attachments.map((attachment) => (
                    <li key={attachment.id}>
                      <span>{attachment.file.name}</span>
                      <span className="t-caption">{attachment.progress > 0 ? `${attachment.progress}%` : "Ready"}</span>
                      <Button type="button" variant="ghost" onClick={() => setAttachments((current) => current.filter((candidate) => candidate.id !== attachment.id))}>
                        Remove
                      </Button>
                      {attachment.error && <ErrorText>{attachment.error}</ErrorText>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="conversation-form__foot">
              <span id={commentCountId} className="t-caption">
                {body.length.toLocaleString()} / 10,000
              </span>
              <Button type="submit" variant={submitVariant} disabled={!validComment || send.isPending}>
                {send.isPending ? "Sending…" : submitLabel}
              </Button>
            </div>
            {send.isError && <ErrorText aria-label="Reply error">{commentErrorMessage(send.error)}</ErrorText>}
            {send.isSuccess && (
              <p className="t-body-sm conversation-sent" role="status" aria-label="Reply sent">
                {successMessage}
              </p>
            )}
          </fieldset>
        </form>
      ) : (
        <p className="t-caption conversation-read-only">{readOnlyMessage}</p>
      )}
    </section>
  );
};
